import clsx from 'clsx';
import useHasGroupWithAiCourse from 'core/hooks/useHasGroupWithAiCourse';
import { MAIN_CONTENT_ID } from 'core/layout/constants';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import { BackButton, Button, Icon, Switch } from 'ui';
import { TEACHER_DASHBOARD_FEATURES } from '../../../constants/teacherDashboardFeatures';
import { AnalyticsFiltersContext } from '../../../contexts/AnalyticsFiltersContext';
import useTeacherDashboardFeatures from '../../../hooks/useTeacherDashboardFeatures';
import SidebarFeedback from '../../molecules/SidebarFeedback/SidebarFeedback';
import './teacher-layout-wrapper.scss';

const NAV_SKELETON_ROWS = [0, 1, 2];

const TeacherLayoutWrapper = ({
  children,
  activeNavigationId,
  onNavigationChange,
  className = '',
  contentGap = 'md',
  contentMode = 'flow',
  gutter = 'standard',
  header = null,
  sidebarMode = 'expanded',
  showBackArrowWhenCollapsed = false,
  toolbar = null,
}) => {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const { features: teacherDashboardFeatures, isResolved: areFeaturesResolved } = useTeacherDashboardFeatures();
  // Radar and Analytics are scoped to the AI courses inside the teacher's groups,
  // so a teacher whose groups have no AI course has nothing to show — hide both.
  // (Access to the feature itself is gated separately by the tenant flags.)
  const { hasGroupWithAiCourse, isLoading: isRosterLoading } = useHasGroupWithAiCourse();
  const radarEnabled = teacherDashboardFeatures[TEACHER_DASHBOARD_FEATURES.RADAR] && hasGroupWithAiCourse;
  const analyticsPanelEnabled = teacherDashboardFeatures[TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL] && hasGroupWithAiCourse;
  const aiToolsEnabled = teacherDashboardFeatures[TEACHER_DASHBOARD_FEATURES.AI_TOOLS];
  // Both gates are fail-open while loading, so rendering their optimistic values
  // would flash items that disappear once the real flags land — hold a skeleton
  // until both are final and paint the menu once.
  const isMenuLoading = !areFeaturesResolved || isRosterLoading;
  const analyticsFilters = useContext(AnalyticsFiltersContext);
  const isMockMode = analyticsFilters?.isMockMode ?? false;
  const handleToggleMockData = analyticsFilters?.handleToggleMockData;
  const [userSidebarCollapsed, setUserSidebarCollapsed] = useState(false);
  const shouldShowMockToggle = activeNavigationId === 'radar' || activeNavigationId === 'analytics';
  const isFillMode = contentMode === 'fill';

  // Determine sidebar collapsed state based on mode
  const sidebarCollapsed = sidebarMode === 'collapsed' ? true : sidebarMode === 'expanded' ? false : userSidebarCollapsed; // user-controlled

  // Show collapse button only in user-controlled mode
  const showCollapseButton = sidebarMode === 'user-controlled';

  // Fixed title for the teacher dashboard
  const title = t('teacher_dashboard_title');

  // Fixed navigation items, gated per tenant dashboard feature toggle.
  const navigationItems = [
    ...(radarEnabled
      ? [
          {
            id: 'radar',
            name: t('radar_title'),
            icon: 'radar',
            onClick: () => {
              history.push('/radar');
            },
          },
        ]
      : []),
    ...(radarEnabled
      ? [
          {
            id: 'students',
            name: t('students_title'),
            icon: 'students',
            onClick: () => {
              history.push('/students');
            },
          },
        ]
      : []),
    ...(analyticsPanelEnabled
      ? [
          {
            id: 'analytics',
            name: t('ai_analytics_panel_title'),
            icon: 'graphic',
            onClick: () => {
              history.push('/analytics');
            },
          },
        ]
      : []),
    ...(aiToolsEnabled
      ? [
          {
            id: 'tools',
            name: t('teacher_tools_section'),
            icon: 'ai',
            onClick: () => {
              history.push('/teacher-tools');
            },
          },
          {
            id: 'history',
            name: t('teacher_tools_history_section'),
            icon: 'replay-history',
            onClick: () => {
              history.push('/teacher-tools/history');
            },
          },
        ]
      : []),
  ];

  const handleClose = () => {
    history.push('/');
  };

  const handleNavigationClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (onNavigationChange) {
      onNavigationChange(item);
    }
  };

  const toggleSidebar = () => {
    setUserSidebarCollapsed(!userSidebarCollapsed);
  };

  const updatedNavigationItems = navigationItems.map((item) => ({
    ...item,
    active: item.id === activeNavigationId,
    onClick: () => handleNavigationClick(item),
  }));

  return (
    <div
      className={`teacher-layout-wrapper ${sidebarCollapsed ? 'teacher-layout-wrapper--collapsed' : ''} ${sidebarCollapsed && showBackArrowWhenCollapsed ? 'teacher-layout-wrapper--show-back-collapsed' : ''} ${className}`}
    >
      {/* Sidebar */}
      <aside className="teacher-layout-wrapper__sidebar">
        <div className="teacher-layout-wrapper__sidebar-header">
          <BackButton onClick={handleClose} />
          {showCollapseButton && (
            <Button
              className="teacher-layout-wrapper__collapse-btn"
              variant="plain"
              isIconOnly
              leftIcon={sidebarCollapsed ? 'angle-right' : 'angle-left'}
              ariaLabel={sidebarCollapsed ? t('chatbot:Expand sidebar') : t('chatbot:Collapse sidebar')}
              ariaExpanded={!sidebarCollapsed}
              onClick={toggleSidebar}
            />
          )}
        </div>

        <div className="teacher-layout-wrapper__title-container">
          <h2 className="teacher-layout-wrapper__title">{title}</h2>
        </div>
        <nav aria-label={t('chatbot:sidebar_nav_label')} className="teacher-layout-wrapper__navigation">
          {/* Unified Navigation */}
          {isMenuLoading ? (
            <ul aria-busy="true" aria-label={t('Loading')} className="teacher-layout-wrapper__nav-list teacher-layout-wrapper__nav-skeleton" role="status">
              {NAV_SKELETON_ROWS.map((row) => (
                <li className="teacher-layout-wrapper__nav-skeleton-item" key={row}>
                  <Skeleton circle height={24} width={24} />
                  <span className="teacher-layout-wrapper__nav-skeleton-text">
                    <Skeleton height={16} />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="teacher-layout-wrapper__nav-list">
              {updatedNavigationItems.map((item) => (
                <li key={item.id}>
                  <button
                    className={`teacher-layout-wrapper__nav-item ${item.active ? 'teacher-layout-wrapper__nav-item--active' : ''}`}
                    onClick={item.onClick}
                    title={item.name}
                  >
                    <Icon name={item.icon} size="lg" />
                    <span className="teacher-layout-wrapper__nav-text">{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </nav>
        <div className="teacher-layout-wrapper__sidebar-footer">
          <SidebarFeedback isCollapsed={sidebarCollapsed} />
          {shouldShowMockToggle && (
            <div className="teacher-layout-wrapper__dev-toggle">
              <Switch label={t('chatbot:mock_toggle_label')} onChange={handleToggleMockData} checked={isMockMode} labelPosition="before" />
              <p className="teacher-layout-wrapper__dev-toggle-hint">
                {isMockMode ? t('chatbot:mock_toggle_hint_active') : t('chatbot:mock_toggle_hint_inactive')}
              </p>
            </div>
          )}
        </div>
      </aside>
      {/* Main Content */}
      <main className={clsx('teacher-layout-wrapper__main', isFillMode && 'teacher-layout-wrapper__main--fill')} id={MAIN_CONTENT_ID} tabIndex={-1}>
        {header}
        {toolbar && <div className={clsx('teacher-layout-wrapper__toolbar', `teacher-layout-wrapper__toolbar--gutter-${gutter}`)}>{toolbar}</div>}
        <div
          className={clsx(
            'teacher-layout-wrapper__content',
            `teacher-layout-wrapper__content--gutter-${gutter}`,
            `teacher-layout-wrapper__content--gap-${contentGap}`,
            isFillMode && 'teacher-layout-wrapper__content--fill'
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

TeacherLayoutWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  activeNavigationId: PropTypes.string,
  onNavigationChange: PropTypes.func,
  className: PropTypes.string,
  contentGap: PropTypes.oneOf(['none', 'xxs', 'xs', 'sm', 'md']),
  contentMode: PropTypes.oneOf(['flow', 'fill']),
  gutter: PropTypes.oneOf(['standard', 'compact', 'compact-start', 'none']),
  header: PropTypes.node,
  sidebarMode: PropTypes.oneOf(['expanded', 'collapsed', 'user-controlled']),
  showBackArrowWhenCollapsed: PropTypes.bool,
  toolbar: PropTypes.node,
};

TeacherLayoutWrapper.defaultProps = {
  className: '',
  contentGap: 'md',
  contentMode: 'flow',
  gutter: 'standard',
  header: null,
  sidebarMode: 'expanded',
  toolbar: null,
};

export default TeacherLayoutWrapper;
